import axios, { AxiosInstance } from 'axios';

/**
 * Resolve the API base URL based on hostname and environment.
 * Production domains → production API, staging → staging API, dev → localhost.
 */
function getProductionSafeApiUrl(): string {
  const hostname = window.location.hostname;
  const envApiUrl = process.env.REACT_APP_API_URL;

  // Staging domain detection
  if (hostname.includes('staging-') && hostname.includes('arkxla.com')) {
    return 'https://staging-api.arkxla.com/api/v1';
  }
  if (hostname === 'staging.arkxla.com') {
    return 'https://staging-api.arkxla.com/api/v1';
  }

  // Production domain detection
  if (hostname.includes('arkxla.com') || hostname.includes('arkx.com')) {
    return 'https://api.arkxla.com/api/v1';
  }

  // Use environment variable if available
  if (envApiUrl) {
    if (process.env.NODE_ENV === 'production' && envApiUrl.startsWith('http://')) {
      return envApiUrl.replace('http://', 'https://');
    }
    return envApiUrl;
  }

  // Development fallback
  return 'http://localhost:8000/api/v1';
}

/**
 * Create a configured axios instance with auth interceptors.
 * Each service module calls this once to get its own reference,
 * but they all share the same config and interceptor logic.
 */
export function createApiClient(): AxiosInstance {
  let baseURL = getProductionSafeApiUrl();

  // Normalize version
  if (baseURL.endsWith('/api/v2') || baseURL.endsWith('/api/v3')) {
    baseURL = baseURL.replace(/\/api\/v[23]$/, '/api/v1');
  }

  // Production safety — never allow HTTP
  if (process.env.NODE_ENV === 'production' && baseURL.startsWith('http://')) {
    baseURL = baseURL.replace('http://', 'https://');
  }

  const api = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor — attach auth token
  api.interceptors.request.use(
    (config) => {
      if (process.env.NODE_ENV === 'production' && config.baseURL?.startsWith('http://')) {
        config.baseURL = config.baseURL.replace('http://', 'https://');
      }

      const url = config.url || '';
      const isCustomerEndpoint =
        url.includes('/shop/customers') ||
        url.includes('/wishlist') ||
        url.includes('/shop/orders');

      const token = isCustomerEndpoint
        ? localStorage.getItem('customer_access_token')
        : localStorage.getItem('access_token');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor — handle 401
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const url = error.config?.url || '';
        const isCustomerEndpoint =
          url.includes('/shop/customers') ||
          url.includes('/wishlist') ||
          url.includes('/shop/orders');

        if (isCustomerEndpoint) {
          localStorage.removeItem('customer_access_token');
          localStorage.removeItem('customer-auth-storage');
        }
      }
      return Promise.reject(error);
    },
  );

  return api;
}

/**
 * Get the raw base URL (for health checks that need to strip /api/v1).
 */
export function getBaseURL(): string {
  return getProductionSafeApiUrl();
}
